import { forwardRef } from "react";
import {
  InformationCircleIcon,
  PlusIcon,
  RefreshIcon,
  SearchIcon,
  QuestionMarkCircleIcon,
} from "@heroicons/react/outline";
import ColumnChooser from "./ColumnChooser";

const FilterSelect = ({ label, value, onChange, options, allLabel = "Όλες" }) => (
  <label className={`ds-filter ${value ? "is-active" : ""}`}>
    <span className="ds-filter-label">{label}</span>
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{allLabel}</option>
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  </label>
);

// Search, counts, actions and filters above a desktop case table.
// `filterDefs` are already resolved: [{ key, label, allLabel?, options }].
// The add / check-all buttons render only when their handler is given.
const CasesToolbar = forwardRef(
  (
    {
      search,
      onSearch,
      searchPlaceholder,
      noun = { one: "υπόθεση", many: "υποθέσεις" },
      filters,
      onFilter,
      filterDefs = [],
      activeFilterCount,
      onClearFilters,
      info,
      total,
      filteredTotal,
      addLabel = "Νέα υπόθεση",
      onAddCase,
      onCheckAll,
      bulkChecking,
      columns,
      visibleColumnIds,
      onToggleColumn,
      onResetColumns,
      onShowHelp,
      extra,
    },
    searchRef
  ) => (
    <div className="ds-toolbar">
      <div className="ds-toolbar-row">
        <label className="ds-search">
          <SearchIcon aria-hidden="true" />
          <input
            ref={searchRef}
            type="search"
            placeholder={searchPlaceholder}
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            aria-label={`Αναζήτηση: ${noun.many}`}
          />
          <kbd className="ds-kbd" aria-hidden="true">
            /
          </kbd>
        </label>
        <span className="ds-count">
          {filteredTotal === total ? (
            <>
              <strong>{total}</strong> {total === 1 ? noun.one : noun.many}
            </>
          ) : (
            <>
              <strong>{filteredTotal}</strong> από {total} {noun.many}
            </>
          )}
        </span>
        {extra}
        <div className="ds-toolbar-spacer" />
        {onAddCase && (
          <button type="button" className="ds-btn ds-btn--primary" onClick={onAddCase}>
            <PlusIcon aria-hidden="true" />
            {addLabel}
          </button>
        )}
        {onCheckAll && (
          <button
            type="button"
            className="ds-btn"
            onClick={onCheckAll}
            disabled={bulkChecking || total === 0}
            title="Μαζικός έλεγχος όλων των υποθέσεων (μία φορά την ημέρα)"
          >
            <RefreshIcon aria-hidden="true" className={bulkChecking ? "ds-spin" : ""} />
            {bulkChecking ? "Γίνεται εκκίνηση…" : "Έλεγχος όλων"}
          </button>
        )}
        <ColumnChooser
          columns={columns}
          visibleIds={visibleColumnIds}
          onToggle={onToggleColumn}
          onReset={onResetColumns}
        />
        <button
          type="button"
          className="ds-icon-btn"
          onClick={onShowHelp}
          aria-label="Συντομεύσεις πληκτρολογίου"
          title="Συντομεύσεις πληκτρολογίου ( ? )"
        >
          <QuestionMarkCircleIcon aria-hidden="true" />
        </button>
      </div>
      {(filterDefs.length > 0 || info) && (
        <div className="ds-toolbar-row ds-filters">
          {filterDefs.map((def) => (
            <FilterSelect
              key={def.key}
              label={def.label}
              value={filters[def.key] ?? ""}
              onChange={(v) => onFilter(def.key, v)}
              options={def.options}
              allLabel={def.allLabel}
            />
          ))}
          {activeFilterCount > 0 && (
            <button type="button" className="ds-link-btn" onClick={onClearFilters}>
              Καθαρισμός φίλτρων ({activeFilterCount})
            </button>
          )}
          {info && (
            <span className="ds-info" tabIndex={0} title={info} aria-label={info}>
              <InformationCircleIcon aria-hidden="true" />
            </span>
          )}
        </div>
      )}
    </div>
  )
);

export default CasesToolbar;
